import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/services/api";

const AlertContext = createContext();

// Polling interval: 30 seconds
const POLL_INTERVAL = 30000;

export function AlertProvider({ children }) {
  const [alertCount, setAlertCount] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch unread count (lightweight endpoint for badge)
  const fetchCount = useCallback(async () => {
    try {
      const res = await api.get('/alertas/count');
      setAlertCount(res.data.count || 0);
    } catch (err) {
      // Silently fail — user might not be authenticated yet
      if (err.response?.status !== 401) {
        console.error("Error fetching alert count:", err);
      }
    }
  }, []);

  // Fetch full alerts list (when dropdown opens)
  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/alertas?solo_no_leidas=false&limit=20');
      setAlerts(res.data.items || []);
    } catch (err) {
      console.error("Error fetching alerts:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark single alert as read
  const markAsRead = useCallback(async (alertaId) => {
    try {
      await api.put(`/alertas/${alertaId}/leer`, {});
      setAlerts((prev) =>
        prev.map((a) => (a._id === alertaId ? { ...a, leida: true } : a))
      );
      setAlertCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marking alert as read:", err);
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await api.put('/alertas/leer-todas', {});
      setAlerts((prev) => prev.map((a) => ({ ...a, leida: true })));
      setAlertCount(0);
    } catch (err) {
      console.error("Error marking all alerts as read:", err);
    }
  }, []);

  // Toggle dropdown
  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) {
        // Opening — fetch fresh data
        fetchAlerts();
      }
      return !prev;
    });
  }, [fetchAlerts]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Polling for count
  useEffect(() => {
    fetchCount(); // initial fetch
    const interval = setInterval(fetchCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchCount]);

  return (
    <AlertContext.Provider
      value={{
        alertCount,
        alerts,
        isOpen,
        loading,
        toggleDropdown,
        closeDropdown,
        markAsRead,
        markAllAsRead,
        fetchCount,
        fetchAlerts,
      }}
    >
      {children}
    </AlertContext.Provider>
  );
}

export function useAlerts() {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlerts must be used within an AlertProvider");
  }
  return context;
}
